import basketUtils from 'utils/Basket';
import orderUtils from 'utils/Order';
import ErrorsUtils from 'utils/Errors';
import localeUtils from 'utils/LanguageLocale';
import checkoutApi from 'services/api/checkout';
import locationUtils from 'utils/Location';
import store from 'Store';
import userActions from 'actions/UserActions';
import userUtils from 'utils/User';
import checkoutUtils from 'utils/Checkout';
import { showModalError, setReady, ERROR_TYPES } from 'actions/PayPalActions';
import OrderActions from 'actions/OrderActions';
import Actions from 'Actions';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS, SHIPPING_METHODS } from 'constants/Checkout';
import { FULFILLMENT_TYPES } from 'actions/ActionsConstants';
import { BASKET_TYPES } from 'constants/RwdBasket';
import PayPalExpressBindings from 'analytics/bindingMethods/components/rwdBasket/PayPalExpressBindings';
import FrictionlessCheckoutUtils from 'utils/FrictionlessCheckout';
import PayPalCheckoutEN from 'utils/locales/PayPalCheckout_en_US';
import PayPalCheckoutFR from 'utils/locales/PayPalCheckout_fr_CA';

const { getLocalisedDataFromResource } = localeUtils;
const getText = getLocalisedDataFromResource(PayPalCheckoutEN, PayPalCheckoutFR);

let paypalCheckoutInstance;
let paypalToken;
let paypalSessionDetails;
let tokenPromise = null;
let isSDKLoaded = false;
let sdkLoadPromise = null;
const renderedContainers = {};
const renderInProgress = {};

// PayPal button style constants
const PAYPAL_BUTTON_STYLE = {
    LAYOUT: 'horizontal',
    COLOR: 'gold',
    SHAPE: 'pill',
    HEIGHT: 44
};

const PAYPAL_BUTTON_LABELS = {
    PAYPAL: 'paypal',
    PAY_WITH_PAYPAL: 'pay'
};

// PayPal SDK supported locales for Sephora (US and CA only)
const PAYPAL_SUPPORTED_LOCALES = {
    /* eslint-disable camelcase */
    en_US: 'en_US',
    en_CA: 'en_CA',
    fr_CA: 'fr_CA'
    /* eslint-enable camelcase */
};

const PayPalCheckout = {
    /**
     * Check if PayPal Express parameters should be included in token request
     * Used to determine if express checkout parameters should be sent to backend
     * Requires all flags to be enabled:
     * 1. PayPal must be enabled (isPayPalEnabled = true)
     * 2. PayPal Pay Now must be enabled (isPayPalPayNowEnabled = true)
     * 3. Frictionless Checkout R3 or R4 must be enabled (cookie + config)
     * 4. User authentication check:
     *    - Logged-in users: Allowed for STH and SDD only (BOPIS excluded)
     *    - Guest users: Allowed for STH orders only (SDD and BOPIS excluded)
     */
    isPayPalExpressParametersEnabled: function () {
        // PayPal global enable flag (when true, PayPal is enabled)
        const isPayPalEnabled = Sephora.configurationSettings.isPayPalEnabled;

        // PayPal Pay Now flag
        const isPayPalPayNowEnabled = Sephora.configurationSettings.isPayPalPayNowEnabled?.isEnabled;

        // Frictionless Checkout Release 3 or 4 flags (cookie AND config must both be true)
        const isFCR3Enabled = FrictionlessCheckoutUtils.isFCReleaseThreeEnabled();
        const isFCR4Enabled = FrictionlessCheckoutUtils.isFCReleaseFourEnabled();
        const isFrictionlessCheckoutEnabled = isFCR3Enabled || isFCR4Enabled;

        // User authentication and fulfillment type validation
        const isGuest = userUtils.isAnonymous();
        const fulfillmentType = PayPalCheckout.getFulfillmentType();

        // BOPIS excluded for all users (logged-in and guest)
        // Guest users: Only STH (Ship To Home) is allowed
        // Logged-in users: STH and SDD allowed (BOPIS excluded)
        // Disabled BOPIS for failing submitOrder api and excluded in requirement PAY-6297
        const isUserEligible = fulfillmentType !== FULFILLMENT_TYPES.PICKUP && (!isGuest || fulfillmentType === FULFILLMENT_TYPES.SHIP);

        const isExpressEnabled = isPayPalEnabled && isPayPalPayNowEnabled && isFrictionlessCheckoutEnabled && isUserEligible;

        // Log feature flag evaluation for debugging
        Sephora.logger.info('PayPal Express: Feature flag evaluation', {
            isExpressEnabled,
            flags: {
                isPayPalEnabled,
                isPayPalPayNowEnabled,
                isFCR3Enabled,
                isFCR4Enabled,
                isFrictionlessCheckoutEnabled
            },
            user: {
                isGuest,
                fulfillmentType,
                isUserEligible
            }
        });

        return isExpressEnabled;
    },

    getOrderId: function () {
        if (locationUtils.isCheckout()) {
            return orderUtils.getOrderId();
        }

        // Basket page
        return basketUtils.getOrderId();
    },

    getFulfillmentType: function () {
        const isPickupOrder = basketUtils.isPickup();
        const basket = store.getState().basket;

        if (isPickupOrder) {
            return FULFILLMENT_TYPES.PICKUP;
        }

        // Check for SAMEDAY
        const hasSameDayItems = basket?.itemsByBasket?.some(item => item.basketType === BASKET_TYPES.SAMEDAY_BASKET);

        if (hasSameDayItems) {
            return FULFILLMENT_TYPES.SAMEDAY;
        }

        // Default to SHIP
        return FULFILLMENT_TYPES.SHIP;
    },

    fetchAndSetupPayPalToken: async function () {
        try {
            const orderId = PayPalCheckout.getOrderId();
            const fulfillmentType = PayPalCheckout.getFulfillmentType();
            const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;

            // Build token request options
            const tokenOptions = {
                orderId,
                fulfillmentType
            };

            // If PayPal Express parameters are enabled, include them in token request
            const includeExpressParams = PayPalCheckout.isPayPalExpressParametersEnabled();

            if (includeExpressParams) {
                tokenOptions.isExpressCheckout = true;

                // For SDD orders, also include shipping method for auto-selection
                if (isSddOrder) {
                    tokenOptions.shippingMethodId = SHIPPING_METHODS.SAME_DAY_UNSCHEDULED;
                }

                Sephora.logger.info('PayPal: Fetching token with express parameters', {
                    orderId,
                    fulfillmentType,
                    shippingMethodId: tokenOptions.shippingMethodId,
                    isExpressCheckout: tokenOptions.isExpressCheckout
                });
            } else {
                Sephora.logger.info('PayPal: Fetching token (standard parameters)', {
                    orderId,
                    fulfillmentType,
                    isSddOrder,
                    includeExpressParams
                });
            }

            const { token, paypalSessionDetails: sessionDetails } = await checkoutApi.getPayPalToken(tokenOptions);
            paypalToken = token;
            paypalSessionDetails = sessionDetails;
            await PayPalCheckout.paypalCheckoutSetup({ clientToken: token });

            Sephora.logger.info('PayPal: Token fetched and setup complete', {
                orderId,
                hasToken: !!token,
                hasSessionDetails: !!sessionDetails
            });
        } catch (errorData) {
            Sephora.logger.error('PayPal: Token fetch failed', {
                error: errorData,
                orderId: PayPalCheckout.getOrderId()
            });
            ErrorsUtils.collectAndValidateBackEndErrors(errorData);
            throw errorData;
        } finally {
            tokenPromise = null;
        }
    },

    preparePaypalCheckout: async function () {
        // If we already have an instance, we're done
        if (paypalCheckoutInstance) {
            return;
        }

        // If we already have a token, just set up the instance
        if (paypalToken) {
            await PayPalCheckout.paypalCheckoutSetup({ clientToken: paypalToken });

            return;
        }

        // If a request is in progress, wait for it
        if (!tokenPromise) {
            tokenPromise = PayPalCheckout.fetchAndSetupPayPalToken();
        }

        await tokenPromise;
    },

    /**
     * Initialize PayPal payment group when PayPal radio button is selected.
     * This follows the same pattern as Venmo, Klarna, Afterpay, and Paze.
     */
    initializePayPalPaymentGroup: async function () {
        try {
            await decorators.withInterstice(checkoutApi.initializePayPalCheckout, INTERSTICE_DELAY_MS)({});

            const order = await checkoutApi.getOrderDetails(orderUtils.getOrderId());
            store.dispatch(OrderActions.updateOrder(order));
            await PayPalCheckout.preparePaypalCheckout();
        } catch (err) {
            store.dispatch(Actions.showInterstice(false));
            Sephora.logger.error(err);
        }
    },

    getPaymentAmount: function () {
        const isPickupOrder = basketUtils.isPickup();
        const amountWithCurrency = locationUtils.isCheckout()
            ? orderUtils.getPayPalAmount() || orderUtils.getCreditCardTotal()
            : basketUtils.getSubtotal(true, isPickupOrder);

        return {
            amountWithCurrency,
            amount: amountWithCurrency ? Number(basketUtils.removeCurrency(amountWithCurrency)) : 0,
            isPickupOrder
        };
    },

    getPaymentConfig: function () {
        const { payPalnonVaulting = false } = Sephora.configurationSettings;
        const currency = localeUtils.isCanada() ? 'CAD' : 'USD';
        const flow = payPalnonVaulting ? 'checkout' : 'vault';

        return {
            payPalnonVaulting,
            currency,
            flow
        };
    },

    /**
     * Get PayPal SDK locale with fallback for unsupported locales
     * PayPal SDK only supports specific locale combinations.
     * @returns {string} Supported PayPal locale (en_US, en_CA, or fr_CA)
     */
    getPayPalLocale: function () {
        const currentLocale = localeUtils.getCurrentLanguageCountryCode();

        // Return locale if it's in the supported whitelist
        if (PAYPAL_SUPPORTED_LOCALES[currentLocale]) {
            return currentLocale;
        }

        // Fallback to en_CA for Canadian users, en_US for all others
        return localeUtils.isCanada() ? PAYPAL_SUPPORTED_LOCALES.en_CA : PAYPAL_SUPPORTED_LOCALES.en_US;
    },

    shouldSendContactInformation: function () {
        if (userUtils.isAnonymous()) {
            return PayPalCheckout.getFulfillmentType() === FULFILLMENT_TYPES.SHIP;
        }

        return true;
    },

    getContactOverrides: function () {
        if (!PayPalCheckout.shouldSendContactInformation()) {
            return null;
        }

        if (userUtils.isAnonymous()) {
            return { contactPreference: 'UPDATE_CONTACT_INFO' };
        }

        const email = userUtils.getProfileEmail();
        const phone = userUtils.getUserPhoneNumber();
        const recipient = {
            ...(email && { recipientEmail: email }),
            ...(phone && {
                internationalPhone: {
                    countryCode: '1',
                    nationalNumber: String(phone).replace(/\D/g, '')
                }
            })
        };

        if (Object.keys(recipient).length === 0) {
            return null;
        }

        return {
            contactPreference: 'RETAIN_CONTACT_INFO',
            recipient
        };
    },

    loadPayPalSDK: async function () {
        // If SDK is already loaded, we're done
        if (isSDKLoaded) {
            return;
        }

        // If SDK is currently loading, wait for it
        if (sdkLoadPromise) {
            await sdkLoadPromise;

            return;
        }

        // Start loading the SDK
        const { payPalnonVaulting, currency } = PayPalCheckout.getPaymentConfig();
        const locale = PayPalCheckout.getPayPalLocale();

        sdkLoadPromise = paypalCheckoutInstance
            .loadPayPalSDK({
                vault: !payPalnonVaulting,
                currency: currency,
                locale: locale,
                intent: payPalnonVaulting ? 'capture' : 'tokenize'
            })
            .then(() => {
                isSDKLoaded = true;
                sdkLoadPromise = null;
            })
            .catch(err => {
                sdkLoadPromise = null;
                throw err;
            });

        await sdkLoadPromise;
    },

    getShippingGroupIdForFulfillmentType: function () {
        const orderDetails = store.getState().order?.orderDetails;
        const fulfillmentType = PayPalCheckout.getFulfillmentType();

        if (fulfillmentType === FULFILLMENT_TYPES.SAMEDAY) {
            return orderUtils.getSameDayShippingGroup(orderDetails)?.shippingGroupId;
        }

        // SHIP and PICKUP both resolve to the HardGoodShippingGroup; PICKUP just has a store address.
        return orderUtils.getHardGoodShippingGroup(orderDetails)?.shippingGroupId;
    },

    getShippingCallbackURL: function () {
        const { protocol, host } = locationUtils.getLocation();
        const shippingGroupId = PayPalCheckout.getShippingGroupIdForFulfillmentType();
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const sessionSegment = encodeURIComponent(paypalSessionDetails ?? '');
        const params = new URLSearchParams({
            shippingGroupId: shippingGroupId ?? '',
            fulfillmentType: fulfillmentType
        });

        return `${protocol}//${host}/api/checkout/paypal/shipping-callback/${sessionSegment}?${params.toString()}`;
    },

    createPaymentOptions: function () {
        const { amountWithCurrency, amount, isPickupOrder } = PayPalCheckout.getPaymentAmount();
        const { payPalnonVaulting, currency, flow } = PayPalCheckout.getPaymentConfig();
        // const isSddOrder = PayPalCheckout.getFulfillmentType() === FULFILLMENT_TYPES.SAMEDAY;
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;
        const contactOverrides = PayPalCheckout.getContactOverrides();
        const hasShippingOverride = isPickupOrder || Boolean(contactOverrides?.recipient);

        const options = {
            flow: flow,
            amount: amount,
            currency: currency,
            enableShippingAddress: !isSddOrder,
            shippingAddressEditable: !isPickupOrder,
            shippingCallbackURL: PayPalCheckout.getShippingCallbackURL(),
            ...(payPalnonVaulting && { intent: 'capture' }),
            ...(contactOverrides?.contactPreference && { contactPreference: contactOverrides.contactPreference }),
            ...(hasShippingOverride && {
                shippingAddressOverride: {
                    ...(isPickupOrder ? basketUtils.shippingAddressOverride() : {}),
                    ...(contactOverrides?.recipient || {})
                }
            })
        };

        if (!payPalnonVaulting) {
            options.billingAgreementDescription = 'Estimated Total: ' + amountWithCurrency;
        }

        return options;
    },

    hasShippingGroupForCurrentOrder: function () {
        const orderId = PayPalCheckout.getOrderId();
        const orderDetails = store.getState().order?.orderDetails;
        const headerOrderId = orderDetails?.header?.orderId;

        if (!headerOrderId || headerOrderId !== orderId) {
            return false;
        }

        return Boolean(PayPalCheckout.getShippingGroupIdForFulfillmentType());
    },

    createOrder: async function () {
        // Initialize PayPal checkout before opening payment sheet
        await checkoutApi.initializePayPalCheckout({});

        // Just call getOrderDetails if we need to
        if (!PayPalCheckout.hasShippingGroupForCurrentOrder()) {
            const order = await checkoutApi.getOrderDetails(PayPalCheckout.getOrderId());
            store.dispatch(OrderActions.updateOrder(order));
        }

        // Track PayPal Express button click analytics
        const fulfillmentType = PayPalCheckout.getFulfillmentType();
        const pageContext = locationUtils.isCheckout() ? 'checkout' : 'basket';
        PayPalExpressBindings.trackPayPalExpressClick(fulfillmentType, pageContext);

        const createPaymentOptions = PayPalCheckout.createPaymentOptions();

        return paypalCheckoutInstance.createPayment(createPaymentOptions);
    },

    onApprove: function (data) {
        return paypalCheckoutInstance.tokenizePayment(data).then(function (payload) {
            Sephora.logger.info('PayPal payment approved', { data, payload });
        });
    },

    onCancel: function (data) {
        Sephora.logger.info('PayPal payment cancelled', {
            data,
            cancelSource: data?.cancelSource || 'unknown'
        });

        // Show error modal to user (following Venmo pattern)
        store.dispatch(
            showModalError({
                errorType: ERROR_TYPES.CANCELLED
            })
        );
    },

    onError: function (err) {
        Sephora.logger.error('PayPal error', {
            err,
            errorCode: err?.code || 'UNKNOWN',
            errorDetails: err?.message
        });

        // Show error modal to user (following Venmo pattern)
        store.dispatch(
            showModalError({
                errorType: ERROR_TYPES.ERROR
            })
        );
    },

    /**
     * Handles clicks outside the PayPal window
     * Triggers the PayPal SDK background overlay with "Click to Continue" and "Close" options
     * @param {object} data - PayPal click data
     */
    onClickOutside: function (data) {
        Sephora.logger.info('PayPal window lost focus - overlay displayed', { data });

        // The PayPal SDK will automatically show the overlay
        // No additional action needed here
        // User can choose:
        //   1. "Click to Continue" - Handled by PayPal SDK (refocuses window)
        //   2. "Close" - Triggers onCancel callback above
    },

    /**
     * Clean up rendered state for a container (called when component unmounts)
     * @param {object} params - Named parameters
     * @param {string} params.containerId - ID of the container to clean up
     */
    cleanupPayPalButton: function ({ containerId }) {
        delete renderedContainers[containerId];
        delete renderInProgress[containerId];
    },

    /**
     * Handle PayPal button approval
     * @param {object} params - Named parameters
     * @param {object} params.data - PayPal approval data
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     */
    onApproveButton: async function ({ data, isBopis }) {
        Sephora.logger.info('PayPal: Button approved, tokenizing payment', {
            isBopis,
            orderId: PayPalCheckout.getOrderId(),
            fulfillmentType: PayPalCheckout.getFulfillmentType()
        });

        const payload = await paypalCheckoutInstance.tokenizePayment(data);

        return PayPalCheckout.handlePayPalCheckoutPayload({ payload, isBopis });
    },

    /**
     * Create PayPal button configuration object
     * @param {object} params - Named parameters
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     * @returns {object} PayPal button configuration
     */
    createPayPalButtonConfig: function ({ isBopis }) {
        const isCheckout = locationUtils.isCheckout();
        const label = isCheckout ? PAYPAL_BUTTON_LABELS.PAY_WITH_PAYPAL : PAYPAL_BUTTON_LABELS.PAYPAL;

        return {
            fundingSource: window.paypal.FUNDING.PAYPAL,
            createOrder: PayPalCheckout.createOrder,
            onApprove: data => PayPalCheckout.onApproveButton({ data, isBopis }),
            onCancel: PayPalCheckout.onCancel,
            onError: PayPalCheckout.onError,
            // Handle when PayPal window loses focus (background overlay feature)
            // Note: PayPal SDK may handle overlay automatically; when user clicks "Close" on overlay, onCancel is triggered
            onClickOutside: PayPalCheckout.onClickOutside,
            style: {
                layout: PAYPAL_BUTTON_STYLE.LAYOUT,
                color: PAYPAL_BUTTON_STYLE.COLOR,
                shape: PAYPAL_BUTTON_STYLE.SHAPE,
                label: label,
                disableMaxWidth: true,
                height: PAYPAL_BUTTON_STYLE.HEIGHT
            }
        };
    },

    /**
     * Insert PayPal button iframe into the specified container
     * @param containerId - ID of the container element to render the button in
     * @param isBopis - Whether this is a BOPIS checkout
     */
    insertPayPalButtonIframe: async function ({ containerId, isBopis }) {
        try {
            await PayPalCheckout.loadPayPalSDK();

            const container = document.querySelector(`#${containerId}`);

            if (!container) {
                return;
            }

            container.innerHTML = '';

            const buttonConfig = PayPalCheckout.createPayPalButtonConfig({ isBopis });
            const paypalButton = window.paypal.Buttons(buttonConfig);

            const containerExists = document.querySelector(`#${containerId}`);

            if (!containerExists) {
                Sephora.logger.info('PayPal button container removed before render completed');

                return;
            }

            await paypalButton.render(`#${containerId}`);

            // Only mark as rendered if container still exists (component might have unmounted)
            const containerStillExists = document.querySelector(`#${containerId}`);

            if (containerStillExists) {
                renderedContainers[containerId] = true;
            }
        } catch (error) {
            Sephora.logger.error('Error rendering PayPal button:', error);
        } finally {
            delete renderInProgress[containerId];
        }
    },

    /**
     * Render PayPal button to a specific container
     * @param {object} params - Named parameters
     * @param {string} params.containerId - ID of the container element to render the button in
     * @param {boolean} params.isBopis - Whether this is a BOPIS checkout
     */
    renderPayPalButton: async function ({ containerId, isBopis }) {
        if (renderedContainers[containerId]) {
            return;
        }

        if (renderInProgress[containerId]) {
            await renderInProgress[containerId];

            // Check if the render actually succeeded; if not, retry
            if (renderedContainers[containerId]) {
                return;
            }
        }

        if (!paypalCheckoutInstance) {
            Sephora.logger.error('PayPal Checkout instance not initialized');

            return;
        }

        renderInProgress[containerId] = PayPalCheckout.insertPayPalButtonIframe({ containerId, isBopis });
        await renderInProgress[containerId];
    },

    handlePayPalCheckoutPayload: async function ({ payload, isBopis }) {
        const PayPalApiError = {
            UNAUTHORIZED: 403,
            INVALID_LOCALE: -10182
        };

        if (isBopis) {
            payload.RopisCheckout = true;
        }

        try {
            // Show interstice when PayPal payment completes and order initialization begins
            store.dispatch(Actions.showInterstice(true));

            await checkoutApi.initializePayPalCheckout(payload);

            // Populate user email/phone from PayPal, if empty in store
            const user = store.getState().user;

            if (payload && payload.details) {
                const updates = {};

                if (!user.login && payload.details.email) {
                    updates.login = payload.details.email;
                }

                if (!user.phoneNumber && payload.details.phone) {
                    updates.phoneNumber = payload.details.phone;
                }

                if (Object.keys(updates).length > 0) {
                    store.dispatch(userActions.update(Object.assign({}, user, updates)));
                }
            }

            // Check if user is eligible for PayPal Express (direct order placement)
            const fulfillmentType = PayPalCheckout.getFulfillmentType();
            const isExpressEligible = PayPalCheckout.isPayPalExpressParametersEnabled();

            // If eligible for express, place order directly and redirect to order confirmation
            if (isExpressEligible) {
                Sephora.logger.info('PayPal Express: Payment approved, placing order directly', {
                    orderId: PayPalCheckout.getOrderId(),
                    fulfillmentType,
                    isBopis,
                    isExpressEligible
                });

                return await PayPalCheckout.placeOrderDirect();
            }

            // Otherwise, redirect to standard checkout flow
            Sephora.logger.info('PayPal: Payment approved, redirecting to checkout (not eligible for express)', {
                orderId: PayPalCheckout.getOrderId(),
                fulfillmentType,
                isBopis,
                isExpressEligible
            });

            return PayPalCheckout.goToCheckout({ isBopis });
        } catch (reason) {
            // Hide interstice on error
            store.dispatch(Actions.showInterstice(false));

            // Extract error message
            const errorMessage = reason?.errorMessage || reason?.message || 'PayPal checkout initialization failed';
            const isLocaleOrAuthError = reason.errorCode === PayPalApiError.INVALID_LOCALE || reason.errorCode === PayPalApiError.UNAUTHORIZED;

            Sephora.logger.error('PayPal: Checkout initialization failed', {
                error: reason,
                errorCode: reason?.errorCode,
                errorMessage,
                isLocaleOrAuthError,
                orderId: PayPalCheckout.getOrderId()
            });

            // Show error modal to user with backend error messages
            const errorMessages = reason?.errorMessages || [errorMessage];

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('checkoutErrorTitle'),
                    message: errorMessages.join(' ') || getText('checkoutInitializationFailed'),
                    buttonText: getText('okButton')
                })
            );

            // Return undefined for consistent-return rule
            return undefined;
        }
    },

    goToCheckout: function ({ isBopis }) {
        return checkoutUtils
            .initializeCheckout({
                isPaypalFlow: true,
                ropisCheckout: isBopis
            })
            .then(() => checkoutUtils.initOrderSuccess(true, isBopis))
            .catch(reason => {
                store.dispatch(Actions.showInterstice(false));

                return checkoutUtils.initOrderFailure(reason);
            });
    },

    /**
     * Place order directly from PayPal paysheet (Express checkout flow)
     * Bypasses checkout page and redirects to order confirmation on success
     * Stays on basket page on error
     */
    placeOrderDirect: async function () {
        const orderId = PayPalCheckout.getOrderId();
        const fulfillmentType = PayPalCheckout.getFulfillmentType();

        Sephora.logger.info('PayPal Express: Starting direct order placement', {
            orderId,
            fulfillmentType
        });

        try {
            // Note: Interstice already shown in handlePayPalCheckoutPayload when PayPal completes
            // Following Venmo pattern - call placeOrder directly without decorators.withInterstice

            Sephora.logger.info('PayPal Express: Calling placeOrder API', {
                orderId,
                isExpressCheckout: true,
                originOfOrder: 'web'
            });

            // Place order directly - interstice remains visible during API call
            const orderResponse = await checkoutApi.placeOrder({
                orderId,
                originOfOrder: 'web',
                isExpressCheckout: true
            });

            // Track successful order placement
            Sephora.logger.info('PayPal Express: Order placed successfully', {
                orderId: orderResponse.orderId || orderId,
                total: orderResponse.total || orderResponse.orderTotal,
                fulfillmentType
            });

            // Fire analytics for successful PayPal Express order placement
            const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;

            if (isSddOrder) {
                PayPalExpressBindings.trackSddExpressOrderPlacement({
                    orderId: orderResponse.orderId || orderId,
                    total: orderResponse.total || orderResponse.orderTotal
                });
            } else {
                PayPalExpressBindings.trackExpressOrderPlacement({
                    orderId: orderResponse.orderId || orderId,
                    total: orderResponse.total || orderResponse.orderTotal,
                    fulfillmentType
                });
            }

            // Use standard placeOrderSuccess flow - handles cleanup and redirect to order confirmation
            Sephora.logger.info('PayPal Express: Order success, redirecting to confirmation', {
                orderId: orderResponse.orderId || orderId,
                fulfillmentType
            });

            checkoutUtils.placeOrderSuccess(orderResponse);

            // Return undefined for consistent-return rule
            return undefined;
        } catch (error) {
            // Order placement failed - stay on basket page
            const errorReason = error?.message || error?.errorMessage || 'Order placement failed';

            Sephora.logger.error('PayPal Express: Order placement failed, staying on basket page', {
                error,
                errorReason,
                errorCode: error?.errorCode,
                orderId,
                fulfillmentType
            });

            // Track order placement failure
            const isSddOrder = fulfillmentType === FULFILLMENT_TYPES.SAMEDAY;

            if (isSddOrder) {
                PayPalExpressBindings.trackSddExpressFallback(errorReason);
            }

            // Hide loading interstice
            store.dispatch(Actions.showInterstice(false));

            // Show error modal to user with backend error messages
            const errorMessages = error?.errorMessages || [errorReason];

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('orderPlacementErrorTitle'),
                    message: errorMessages.join(' ') || getText('orderPlacementFailed'),
                    buttonText: getText('okButton')
                })
            );

            // Don't throw - allow user to stay on basket page
            return undefined;
        }
    },

    braintreeCreateSuccessCallback: async function ({ clientInstance }) {
        const braintreePayPalCheckout = await import(/* webpackChunkName: "priority" */ 'BraintreePayPalCheckout');

        try {
            const paypalInstance = await braintreePayPalCheckout.create({
                client: clientInstance
            });
            paypalCheckoutInstance = paypalInstance;
            store.dispatch(setReady(true));
        } catch (err) {
            Sephora.logger.error('PayPalCheckout.braintreeCreateSuccessCallback: Error creating Braintree PayPal Checkout', {
                error: err
            });
            throw err;
        }
    },

    paypalCheckoutSetup: async function ({ clientToken }) {
        paypalToken = clientToken;

        const client = await import(/* webpackChunkName: "priority" */ 'BraintreeClient');

        try {
            const clientInstance = await client.create({ authorization: clientToken });
            await PayPalCheckout.braintreeCreateSuccessCallback({ clientInstance });
        } catch (clientErr) {
            Sephora.logger.error('PayPalCheckout.paypalCheckoutSetup: Error creating Braintree Client', {
                error: clientErr,
                hasClientToken: Boolean(clientToken)
            });
            throw clientErr;
        }
    }
};

export default PayPalCheckout;
